/**
 * Closing Costs Calculator Utility
 * Centralized logic for calculating closing costs to avoid duplication
 */

/**
 * Calculate mansion tax based on asking price and thresholds
 */
export const calculateMansionTax = (price, thresholds) => {
  if (!thresholds || !price) return 0;

  let applicableRate = 0;
  for (const threshold of thresholds) {
    if (price >= threshold.min) {
      applicableRate = threshold.rate;
    } else {
      break;
    }
  }

  return Math.round((price * applicableRate) / 100);
};

/**
 * Calculate transfer taxes based on asking price and thresholds
 */
export const calculateTransferTaxes = (price, thresholds) => {
  if (!thresholds || !price) return 0;

  let applicableTotal = 0;
  for (const threshold of thresholds) {
    if (price >= threshold.min) {
      applicableTotal = threshold.total;
    } else {
      break;
    }
  }

  return Math.round((price * applicableTotal) / 100);
};

/**
 * Calculate mortgage recording tax
 * Based on LOAN AMOUNT, not purchase price
 *
 * Resale:
 *   If loan ≤ $500K: 1.8%
 *   If loan > $500K: 1.925%
 *
 * Sponsor:
 *   If loan ≤ $500K: 2.05%
 *   If loan > $500K: 2.175%
 *
 * @returns {Object} { amount, rate, loanAmount } for display purposes
 */
export const calculateMortgageRecordingTax = (loanAmount, config, saleType = "Resale") => {
  if (!config || !loanAmount) return { amount: 0, rate: 0, loanAmount: 0 };

  // Get rates based on sale type (resale vs sponsor)
  const saleTypeLower = (saleType || "Resale").toLowerCase();
  const rates = saleTypeLower === "sponsor" ? config.sponsor : config.resale;

  if (!rates) return { amount: 0, rate: 0, loanAmount: 0 };

  const rate = loanAmount <= config.threshold ? rates.rate_below : rates.rate_above;
  const amount = Math.round((loanAmount * rate) / 100);

  return { amount, rate, loanAmount };
};

/**
 * Calculate Owner's Title Insurance Premium (TIRSA)
 * Uses tiered rate table based on purchase price
 *
 * Rate per $1,000:
 *   First $250K:      $5.00
 *   Next $750K (to $1M): $4.00
 *   Next $1M (to $2M):   $3.00
 *   Next $2M (to $4M):   $2.50
 *   Above $4M:           $2.25
 *
 * @param {number} price - Purchase price
 * @returns {number} Title insurance premium
 */
export const calculateTitleInsurance = (price) => {
  if (!price || price <= 0) return 0;

  let premium = 0;
  let remaining = price;

  // Tier 1: First $250K at $5.00 per $1,000
  const tier1 = Math.min(remaining, 250000);
  premium += (tier1 / 1000) * 5.00;
  remaining -= tier1;

  // Tier 2: Next $750K (to $1M) at $4.00 per $1,000
  if (remaining > 0) {
    const tier2 = Math.min(remaining, 750000);
    premium += (tier2 / 1000) * 4.00;
    remaining -= tier2;
  }

  // Tier 3: Next $1M (to $2M) at $3.00 per $1,000
  if (remaining > 0) {
    const tier3 = Math.min(remaining, 1000000);
    premium += (tier3 / 1000) * 3.00;
    remaining -= tier3;
  }

  // Tier 4: Next $2M (to $4M) at $2.50 per $1,000
  if (remaining > 0) {
    const tier4 = Math.min(remaining, 2000000);
    premium += (tier4 / 1000) * 2.50;
    remaining -= tier4;
  }

  // Tier 5: Above $4M at $2.25 per $1,000
  if (remaining > 0) {
    premium += (remaining / 1000) * 2.25;
  }

  return Math.round(premium);
};

/**
 * Calculate Lender's (Mortgage) Title Insurance Premium
 * Simultaneous issue rate when purchased with owner's policy
 *
 * Rate: $2.75 per $1,000 of loan amount
 *
 * @param {number} loanAmount - Mortgage loan amount
 * @returns {number} Mortgage title insurance premium
 */
export const calculateMortgageTitleInsurance = (loanAmount) => {
  if (!loanAmount || loanAmount <= 0) return 0;
  // Simultaneous issue rate: $2.75 per $1,000
  return Math.round((loanAmount / 1000) * 2.75);
};

/**
 * Calculate total closing costs based on configuration
 * @param {Object} params - Calculation parameters
 * @returns {Object} Breakdown of closing costs
 */
export const calculateClosingCosts = ({
  askingPrice,
  buildingType = "Condo",
  saleType = "Resale",
  financing = "Yes",
  downPaymentPercent = 25, // Default 25% down payment
  config,
  editableFees = {}, // Optional: override default fees
  otherCosts = [], // Optional: additional costs array
  closingCostCredit = 0
}) => {
  if (!config) {
    throw new Error('Config is required for closing costs calculation');
  }

  const fees = config.fees || {};

  // Start with fees (use editable fees if provided, otherwise use config defaults)
  const costs = {
    applicationFee: editableFees.applicationFee ?? fees.application_fee ?? 0,
    appraisalFee: editableFees.appraisalFee ?? fees.appraisal_fee ?? 0,
    creditCheck: editableFees.creditCheck ?? fees.credit_check ?? 0,
    bankAttorney: editableFees.bankAttorney ?? fees.bank_attorney ?? 0,
    recordingFee: editableFees.recordingFee ?? fees.recording_fee ?? 0,
    attorney: editableFees.attorney ?? fees.attorney ?? 0,
    workingCapital: editableFees.workingCapital ?? fees.working_capital ?? 0,
    closerFee: editableFees.closerFee ?? fees.closer_fee ?? 0,
  };

  // Calculate mansion tax
  costs.mansionTax = calculateMansionTax(askingPrice, config.mansion_tax?.thresholds);

  // Calculate transfer taxes (only for Sponsor sales)
  costs.transferTaxes = saleType === "Sponsor"
    ? calculateTransferTaxes(askingPrice, config.transfer_tax?.thresholds)
    : 0;

  // Normalize buildingType for comparison (backend returns lowercase)
  const buildingTypeLower = (buildingType || "").toLowerCase();

  // Calculate loan amount for mortgage recording tax
  const loanAmount = askingPrice * (1 - downPaymentPercent / 100);

  // Calculate mortgage recording tax (only for non-Coop/Condop with financing)
  // Based on LOAN AMOUNT, not purchase price
  // Rates differ for Resale vs Sponsor
  let mrtDetails = { amount: 0, rate: 0, loanAmount: 0 };
  if (buildingTypeLower !== "coop" && buildingTypeLower !== "condop" && financing === "Yes") {
    mrtDetails = calculateMortgageRecordingTax(loanAmount, config.mortgage_recording_tax?.condo, saleType);
  }
  costs.mortgageRecordingTax = mrtDetails.amount;

  // Calculate title insurance (only for non-Coop/Condop)
  // Uses tiered TIRSA rate table based on purchase price
  costs.titleInsurance = (buildingTypeLower !== "coop" && buildingTypeLower !== "condop")
    ? calculateTitleInsurance(askingPrice)
    : 0;

  // Calculate mortgage title insurance (only for non-Coop/Condop with financing)
  // Based on LOAN AMOUNT, simultaneous issue rate: $2.75 per $1,000
  costs.mortgageTitleInsurance = (buildingTypeLower !== "coop" && buildingTypeLower !== "condop" && financing === "Yes")
    ? calculateMortgageTitleInsurance(loanAmount)
    : 0;

  // Calculate totals
  const totalFees = Object.values(costs).reduce((sum, value) => sum + value, 0);
  const totalOtherCosts = otherCosts.reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalBeforeCredit = totalFees + totalOtherCosts;
  const netClosingCosts = totalBeforeCredit - closingCostCredit;

  return {
    costs,
    totalFees,
    totalOtherCosts,
    totalBeforeCredit,
    closingCostCredit,
    netClosingCosts: Math.round(netClosingCosts),
    mrtDetails // { amount, rate, loanAmount } for display
  };
};

/**
 * Calculate default closing costs (for initial page load)
 * Assumes defaults: Resale, financing = Yes
 */
export const calculateDefaultClosingCosts = async (propertyId, askingPrice, buildingType, authFetch) => {
  try {
    const response = await authFetch(`/config/closing_costs?property_id=${propertyId}`);

    if (!response || !response.data) {
      console.warn('No closing costs config found');
      return 0;
    }

    const result = calculateClosingCosts({
      askingPrice,
      buildingType,
      saleType: "Resale", // Default to Resale
      financing: "Yes", // Default to Yes (most common)
      config: response.data,
      closingCostCredit: 0
    });

    return result.netClosingCosts;
  } catch (error) {
    console.error('Error calculating default closing costs:', error);
    return 0;
  }
};
